import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import {
  FrameAction,
  FrameActionType,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { selectPresenceCollection } from '@/features/broadcast-schedule-program/helpers/select-presence-collection';
import { usePresenceCollectionsSync } from '@/features/broadcast-schedule-program/hooks/usePresenceCollectionsSync';
import { useSegmentLogoPermissions } from '@/features/broadcast-schedule-program/hooks/useSegmentLogoPermissions';
import { batchLogoParams, useDeleteSegmentLogo } from '@/features/segments/api/delete-segment-logo';
import { TPostSegmentLogo, TSegmentLogo } from '@/features/segments/types';
import { useCachedLogoLookup } from '@/hooks/use-catalogue-entries-by-id';
import { usePresencePreviewBox } from '@/hooks/use-presence-preview-box';
import { useSegmentPresences } from '@/hooks/use-segment-presences';

import { ESegmentToolkit } from '../../types';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '../BroadcastScheduleProgramBodySegmentToolkitNavigation';

import { BroadcastScheduleProgramBodySegmentToolkitLogotiposForm } from './components/BroadcastScheduleProgramBodySegmentToolkitLogotiposForm';
import { BroadcastScheduleProgramBodySegmentToolkitLogotiposTable } from './components/BroadcastScheduleProgramBodySegmentToolkitLogotiposTable';

export default function BroadcastScheduleProgramBodySegmentToolkitLogotipos() {
  const {
    formBox,
    logoData,
    activeSegment,
    isGlobalView,
    globalViewSegments,
    globalViewOnPreview,
    setVisibleBox,
    setFormBox,
    setLogoData,
    setActiveSegment,
    handleTooltikClick,
    setSegmentItems,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteLogoSegment } = useDeleteSegmentLogo({});
  const findLogo = useCachedLogoLookup();
  const { apply: applyCollectionsSync } = usePresenceCollectionsSync();
  const { t } = useTranslation();

  const logoItems = useMemo(
    () =>
      selectPresenceCollection(
        { isGlobalView, activeSegment, globalViewSegments },
        'segment_logos',
        'logo'
      ),
    [isGlobalView, activeSegment, globalViewSegments]
  );

  const previewPresenceBox = usePresencePreviewBox({
    referenceStartDatetime: activeSegment?.start_datetime,
    setVisibleBox,
    videoControlRef,
  });

  const handlePreviewBox = (
    box: TSegmentLogo,
    frame: FrameActionType = FrameAction.START,
    currentFrame?: number
  ) => {
    if (logoData?.id !== box.id) {
      setLogoData(box);
    }

    previewPresenceBox(box, frame, currentFrame);
  };

  const {
    canCreate: canCreateLogo,
    canUpdate: canUpdateLogo,
    canDelete: canDeleteLogo,
  } = useSegmentLogoPermissions();

  const { handleUpdate } = useSegmentPresences();

  const handleEditLogo = useCallback(
    (item: TSegmentLogo) => {
      if (!canUpdateLogo) return;
      setFormBox('logo');
      setLogoData(item);
    },
    [setFormBox, canUpdateLogo]
  );

  const handleEditLogoInline = useCallback(
    (item: TPostSegmentLogo, silently?: boolean, currentFrame?: number) => {
      if (!canUpdateLogo) return;
      const editLogoSegmentParams: TPostSegmentLogo = {
        ...item,
        silently: silently || false,
      };

      handleUpdate(editLogoSegmentParams).then(() => {
        const logo = findLogo(item.logo_id);
        const itemLogo = {
          ...item,
          kind: 'logo',
          entity_label: logo?.name || '',
          start_datetime: item?.start_datetime?.toString(),
          end_datetime: item?.end_datetime?.toString(),
        } as TSegmentLogo;
        handlePreviewBox(itemLogo, FrameAction.CURRENT, currentFrame);
      });
    },
    [canUpdateLogo]
  );

  const handleCreateLogo = useCallback(() => {
    if (!canCreateLogo) return;
    setFormBox('logo');
    setLogoData(null);
  }, [setFormBox, setLogoData, canCreateLogo]);

  const handleDeleteLogo = useCallback(
    (item: TSegmentLogo, silently?: boolean) => {
      if (!canDeleteLogo) return;
      const deleteLogoSegmentParams: batchLogoParams = {
        id: item.id || '',
        silently,
      };

      deleteLogoSegment(deleteLogoSegmentParams).then(() => {
        setActiveSegment(prev => {
          if (prev) {
            return {
              ...prev,
              segment_logos: prev.segment_logos?.filter(logo => logo.id !== item.id),
            };
          }
          return prev;
        });
        setSegmentItems(prev => {
          if (!prev) return prev;
          return prev.map(segment => {
            if (segment.id === activeSegment?.id) {
              return {
                ...segment,
                segment_logos: segment.segment_logos?.filter(logo => logo.id !== item.id),
              };
            }
            return segment;
          });
        });
      });
    },
    [canDeleteLogo]
  );

  const handleEditLogoInlineGlobal = useCallback(
    (item: TPostSegmentLogo, silently?: boolean, currentFrame?: number) => {
      void currentFrame;
      if (!canUpdateLogo) return;
      handleUpdate({ ...item, silently: silently || false }).then(() => {
        const logo = findLogo(item.logo_id);
        applyCollectionsSync(
          item.segment_id,
          'segment_logos',
          logos =>
            logos.map(l => {
              if (l.id !== item.id) return l;
              return {
                ...l,
                logo_id: item.logo_id,
                entity_score: item.entity_score,
                start_datetime: item.start_datetime?.toString() ?? l.start_datetime,
                end_datetime: item.end_datetime?.toString() ?? l.end_datetime,
                parameters: {
                  ...l.parameters,
                  ...item.parameters,
                  bbox: item.parameters?.bbox ?? l.parameters.bbox,
                },
                entity_label: logo?.name ?? l.entity_label,
                review_state: item.review_state ?? l.review_state,
              } as TSegmentLogo;
            }),
          'global'
        );
      });
    },
    [canUpdateLogo, handleUpdate, findLogo, applyCollectionsSync]
  );

  const handleDeleteLogoGlobal = useCallback(
    (item: TSegmentLogo, silently?: boolean) => {
      if (!canDeleteLogo) return;
      deleteLogoSegment({ id: item.id || '', silently }).then(() => {
        applyCollectionsSync(item.segment_id, 'segment_logos', logos =>
          logos.filter(l => l.id !== item.id)
        );
      });
    },
    [canDeleteLogo, deleteLogoSegment, applyCollectionsSync]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreateLogo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreateLogo]);

  const tableHandlers = isGlobalView
    ? {
        onPreview: globalViewOnPreview
          ? (box: TSegmentLogo, frame?: FrameActionType) => globalViewOnPreview(box, frame)
          : undefined,
        onUpdate: undefined,
        onUpdateInline: canUpdateLogo ? handleEditLogoInlineGlobal : undefined,
        onDelete: canDeleteLogo ? handleDeleteLogoGlobal : undefined,
      }
    : {
        onPreview: handlePreviewBox,
        onUpdate: canUpdateLogo ? handleEditLogo : undefined,
        onUpdateInline: canUpdateLogo ? handleEditLogoInline : undefined,
        onDelete: canDeleteLogo ? handleDeleteLogo : undefined,
      };

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-2 lg:order-3'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'logo' || isGlobalView ? (
          <>
            <GeneralHeader
              name={t('Logotipos')}
              buttonName={!isGlobalView && canCreateLogo ? t('Añadir') : undefined}
              onClick={!isGlobalView ? handleCreateLogo : undefined}
              onClose={() => handleTooltikClick(ESegmentToolkit.LOGOTIPO)}
            />
            <div className="flex max-h-[calc(100vh-236px)] min-h-[400px] flex-col overflow-y-auto">
              <BroadcastScheduleProgramBodySegmentToolkitLogotiposTable
                items={logoItems}
                {...tableHandlers}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <BroadcastScheduleProgramBodySegmentToolkitLogotiposForm />
            {logoData && (
              <BroadcastScheduleProgramBodySegmentToolkitNavigation
                items={activeSegment?.segment_logos || []}
                activeItem={logoData}
                onChangeData={setLogoData}
              />
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
